import React, { useState } from 'react';
import { ChatChannelUser } from '../../../models/ChatChannelUser';
import { ChatChannelEvent } from '../../../models/ChatChannelEvent';
import { ChatChannelGroup } from '../../../models/ChatChannelGroup';
import { ChatChannel } from '../../../models/ChatChannel';
import { FriendshipStatusType } from '../../../models/FriendshipStatus';
import { chatRepository } from '../../../repositories/ChatRepository';
import { useAuth } from '../../../hooks/useAuth';
import { useBlockedUsers } from '../../../hooks/useBlockedUsers';
import { flip, shift, useFloating } from '@floating-ui/react-dom';
import { offset } from '@floating-ui/react-dom';
import { useRouter } from '../../../hooks/useRouter';
import { createPortal } from 'react-dom';
import { plebsRepository } from '../../../repositories/PlebsRepository';

enum ChatChannelAction {
  ViewInfo = 'viewInfo',
  Mute = 'mute',
  Unmute = 'unmute',
  Pin = 'pin',
  Unpin = 'unpin',
  MarkRead = 'markRead',
  MarkUnread = 'markUnread',
  Share = 'share',
  DeleteConnection = 'deleteConnection',
  Block = 'block',
  Leave = 'leave',
  Delete = 'delete'
}

interface ActionConfig {
  description: (channel: ChatChannel) => string;
  icon: string;
  customColor?: string;
  isAvailable: (channel: ChatChannel) => boolean;
}

export const actionConfigs: Record<ChatChannelAction, ActionConfig> = {
    [ChatChannelAction.ViewInfo]: {
      description: (channel) => {
        if(channel instanceof ChatChannelUser) return 'View Profile';
        if(channel instanceof ChatChannelEvent) return 'View Event';
        if(channel instanceof ChatChannelGroup) return 'View Group Info';
        return '';
      },
      icon: 'info',
      isAvailable: (channel) => 
        channel instanceof ChatChannelUser || channel instanceof ChatChannelGroup || channel instanceof ChatChannelEvent
    },
    [ChatChannelAction.Mute]: {
      description: () => 'Mute',
      icon: 'volume_off',
      isAvailable: (channel) => !channel.isMuted
    },
  
    [ChatChannelAction.Unmute]: {
      description: () => 'Unmute', 
      icon: 'volume_up',
      isAvailable: (channel) => !!channel.isMuted
    },

    [ChatChannelAction.Pin]: {
      description: () => 'Pin',
      icon: 'keep',
      isAvailable: (channel) => channel.channel.state?.membership.pinned_at === undefined
    },

    [ChatChannelAction.Unpin]: {
      description: () => 'Unpin',
      icon: 'keep_off',
      isAvailable: (channel) => channel.channel.state?.membership.pinned_at !== undefined
    },
  
    [ChatChannelAction.MarkRead]: {
      description: () => 'Mark Read',
      icon: 'mark_chat_read',
      isAvailable: (channel) => (channel.unreadCount ?? 0) > 0
    },
  
    [ChatChannelAction.MarkUnread]: {
      description: () => 'Mark Unread',
      icon: 'mark_chat_unread',
      isAvailable: (channel) => (channel.unreadCount ?? 0) === 0
    },
  
    [ChatChannelAction.DeleteConnection]: {
      description: (channel) => {
        if(channel.friendshipStatus.type === FriendshipStatusType.RequestReceived) return 'Reject';
        if(channel.friendshipStatus.type === FriendshipStatusType.RequestSent) return 'Cancel Request';
        if(channel.friendshipStatus.type === FriendshipStatusType.Established) return 'Delete Chat & Remove Connection';
        return '';
      },
      icon: 'delete',
      customColor: 'red-500',
      isAvailable: (channel) => channel instanceof ChatChannelUser
    },

    [ChatChannelAction.Share]: {
      description: (channel) => {
        if(channel instanceof ChatChannelEvent) return 'Share Group Chat';
        if(channel instanceof ChatChannelGroup) return 'Share Group Chat';
        if(channel instanceof ChatChannelUser) return 'Share Profile';
        return '';
      },
      icon: 'link',
      isAvailable: (channel) => 
        channel instanceof ChatChannelGroup || channel instanceof ChatChannelEvent || channel instanceof ChatChannelUser
    },
  
    [ChatChannelAction.Block]: {
      description: (channel) => 
        channel.friendshipStatus.type === FriendshipStatusType.RequestReceived ? 
          'Reject & Block' : 'Block',
      icon: 'back_hand',
      customColor: 'red-500',
      isAvailable: (channel) => 
        channel instanceof ChatChannelUser && (
            channel.friendshipStatus.type === FriendshipStatusType.RequestReceived ||
            channel.friendshipStatus.type === FriendshipStatusType.Established
        )
    },
  
    [ChatChannelAction.Leave]: {
      description: () => 'Leave Chat',
      icon: 'logout',
      customColor: 'red-500',
      isAvailable: (channel) => {
        if (channel instanceof ChatChannelEvent) return true;
        if (channel instanceof ChatChannelGroup) {
          const isAdmin = channel.isAdmin;
          const adminCount = channel.adminCount;
          return !isAdmin || (isAdmin && adminCount > 1);
        }
        return false;
      }
    },
  
    [ChatChannelAction.Delete]: {
      description: () => 'Delete Group Chat',
      icon: 'delete',
      customColor: 'red-500',
      isAvailable: (channel) => {
        if (channel instanceof ChatChannelGroup) {
          const isAdmin = channel.isAdmin;
          const adminCount = channel.adminCount;
          return isAdmin && adminCount === 1;
        }
        return false;
      }
    }
  };

interface ChatChannelPopUpMenuProps {
  channel: ChatChannel;
}

export const ChatChannelPopUpMenu: React.FC<ChatChannelPopUpMenuProps> = ({ 
  channel 
}) => {
  const { pleb } = useAuth();
  const { onUserBlocked } = useBlockedUsers();
  const {navigateToProfileFromChatChannel, navigateToEvent} = useRouter();

  const [isOpen, setIsOpen] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen((prev) => !prev);
  }

  // Floating UI Hook to calculate positioning
  const { x, y, strategy, refs } = useFloating({
    placement: "bottom-end",
    middleware: [offset(8), flip(), shift()],
  });


  const handleAction = async (e: React.MouseEvent, action: ChatChannelAction) => {
    e.stopPropagation();

    switch (action) {
      case ChatChannelAction.ViewInfo:
        if(channel instanceof ChatChannelEvent) {
          navigateToEvent(channel.id);
        }
        else {
          navigateToProfileFromChatChannel(channel)
        }
        break;
      case ChatChannelAction.Mute:
      case ChatChannelAction.Unmute:
        await chatRepository.toggleMute(channel);
        break;
      case ChatChannelAction.Pin:
        await chatRepository.pin(channel);
        break;
      case ChatChannelAction.Unpin:
        await chatRepository.unpin(channel);
        break;
      case ChatChannelAction.MarkRead:
        await chatRepository.markRead(channel);
        break;
      case ChatChannelAction.MarkUnread:
        await chatRepository.markUnread(channel, pleb?.id ?? '');
        break;
      case ChatChannelAction.Share:
        if(channel instanceof ChatChannelEvent) {
          const link = await chatRepository.generateDeeplink("event", channel.id);
          navigator.clipboard.writeText(link);
        }
        else if(channel instanceof ChatChannelGroup) {
          const link = await chatRepository.generateDeeplink("group-chat", channel.id);
          navigator.clipboard.writeText(link);
        }
        else if(channel instanceof ChatChannelUser) {
          const user = await plebsRepository.getUserDetails(channel.id, pleb?.id ?? '');
          const link = user.shareLink;
          if(link) {
            navigator.clipboard.writeText(link);
          }
        }
        break;
      case ChatChannelAction.DeleteConnection:
        await chatRepository.deleteConnection(channel);
        break;
      case ChatChannelAction.Block:
        if (await chatRepository.blockUser(channel)) {
          onUserBlocked(channel.id);
        }
        break;
      case ChatChannelAction.Leave:
        if (channel instanceof ChatChannelEvent) {
          await chatRepository.leaveEventChat(channel.id);
        } else if (channel instanceof ChatChannelGroup) {
          await chatRepository.leaveGroupChat(channel.id, pleb?.id ?? '');
        }
        break;
      case ChatChannelAction.Delete:
        if (channel instanceof ChatChannelGroup) {
          await chatRepository.deleteGroupChat(channel.id);
        }
        break;
    }

    setIsOpen(false);
  };

  return (
    <div className="relative inline-block">
      <button
        ref={refs.setReference}
        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full h-[40px] w-[40px] flex items-center justify-center"
        onClick={handleClick}
      >
        <i className="material-icons text-brand-orange">more_vert</i>
      </button>

      {isOpen && (
        createPortal(
          <div
            ref={refs.setFloating}
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0,
            }}
            className="w-60 bg-brand-bg-2 border border-brand-border-color rounded-md shadow shadow-grey dark:shadow dark:shadow-brand-grey"
          >
            {
              Object.values(ChatChannelAction)
              .filter(action => actionConfigs[action].isAvailable(channel))
              .map(action => (
                  <div
                      key={action}
                      className="flex items-center w-full px-4 py-2 hover:bg-gray-100 
                      dark:hover:bg-gray-700 cursor-pointer border-b border-brand-border-color"
                      onClick={(e) => handleAction(e, action)}
                  >
                      <i 
                        className={`material-symbols-outlined text-[20px] mr-2 ${
                          actionConfigs[action].customColor ? 
                          `text-${actionConfigs[action].customColor}` : 
                          'text-current'
                      }`}
                      >
                        {actionConfigs[action].icon}
                      </i>
                      <span className={`text-sm ${
                      actionConfigs[action].customColor ? 
                      `text-${actionConfigs[action].customColor}` : 
                      'text-current'
                      }`}>
                      {actionConfigs[action].description(channel)}
                      </span>
                  </div>
              ))
            }
          </div>,
          document.body
        )
      )}
    </div>
  );
};
